import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = { kind: "ipc", amount: "", currency: "USD", description: "", status: "draft", date: "", file: null };

export function CreatePaymentDialog({ open, onOpenChange, clientName }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [form, setForm] = useState(EMPTY_FORM);

  const createMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("kind", form.kind);
      fd.append("amount", form.amount);
      fd.append("currency", form.currency);
      fd.append("description", form.description);
      fd.append("status", form.status);
      if (form.date) fd.append("date", form.date);
      if (form.file) fd.append("file", form.file);
      return (await api.post(`/projects/${selectedId}/payments`, fd)).data;
    },
    onSuccess: (d) => {
      if (d?.pending) {
        toast.success(t("payments.toastPending"));
        qc.invalidateQueries({ queryKey: ["payment-actions", selectedId] });
      } else {
        toast.success(t("payments.toastAdded"));
      }
      onOpenChange(false);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ["payments", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-payment-dialog">
        <DialogHeader><DialogTitle>{t("payments.createTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("payments.thType")}</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="payment-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ipc" data-testid="payment-kind-ipc">{t("payments.kindIpcLong", { client: clientName })}</SelectItem>
                <SelectItem value="payment" data-testid="payment-kind-payment">{t("payments.kindPaymentLong", { client: clientName })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("payments.amountL")}</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="payment-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("payments.currencyL")}</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="payment-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["AED", "USD", "EUR", "TRY"].map((c) => (
                    <SelectItem key={c} value={c} data-testid={`payment-currency-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="payment-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("common.status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" data-testid="payment-status-draft">{t("badges.draft")}</SelectItem>
                  <SelectItem value="sent" data-testid="payment-status-sent">{t("badges.sent")}</SelectItem>
                  <SelectItem value="paid" data-testid="payment-status-paid">{t("badges.paid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("payments.dateL")}</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("payments.attachL")}</Label>
            <Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="payment-file-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => createMut.mutate()} disabled={!form.amount || createMut.isPending} data-testid="create-payment-submit-button">
            {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("payments.addRecord")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
